/**
 * Load one job by its ticket number, for the pages a scanned label opens
 * (/job/{n}, /job/{n}/summary, /check-in/new?dropoff|return={n}). One loader,
 * so not-a-number, not-found and failure read the same everywhere.
 */

import { useCallback, useEffect, useState } from 'react';

import type { WorkItem } from '../components/workitems/workItemTypes';
import { getWorkItemByNumber } from '../services/workItemDataService';

export function useWorkItemByNumber(ticketNumber: string) {
  const [item, setItem] = useState<WorkItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!/^\d+$/.test(ticketNumber)) {
        setError(`"${ticketNumber}" is not a job number.`);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const found = await getWorkItemByNumber(Number(ticketNumber), signal);
        if (signal?.aborted) return;
        if (!found) setError(`No job found with number ${ticketNumber}.`);
        setItem(found);
      } catch (e) {
        if (signal?.aborted) return;
        setError(e instanceof Error ? e.message : 'Failed to load job');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [ticketNumber],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /** Refetch (e.g. after an undo); keeps the current item while loading. */
  const reload = useCallback(() => load(), [load]);

  return { item, setItem, loading, error, reload };
}
